import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { createSignal, onMount } from "solid-js";
import { getUserManager } from "~/lib/auth";

// Capture the full OIDC callback URL (with state & code query parameters)
// immediately upon module load, before the router can strip or redirect.
const initialUrl =
  typeof window !== "undefined" ? window.location.href : undefined;

export const Route = createFileRoute("/auth/callback")({
  component: CallbackPage,
  // Disable SSR for this route — it needs window.location
  ssr: false,
});

function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = createSignal<string | null>(null);

  onMount(async () => {
    try {
      const user = await getUserManager().signinRedirectCallback(initialUrl);
      const returnHost = (user.state as { returnHost?: string } | null)
        ?.returnHost;

      if (returnHost && returnHost !== window.location.hostname) {
        const fragment = encodeURIComponent(user.toStorageString());
        window.location.href = `https://${returnHost}/auth/handoff#${fragment}`;
        return;
      }

      navigate({ to: "/dashboard", replace: true });
    } catch (err) {
      console.error("OIDC callback error:", err);
      setError(err instanceof Error ? err.message : "Authentication failed");
    }
  });

  return (
    <div class="min-h-screen flex items-center justify-center p-4">
      {error() ? (
        <div class="w-full max-w-sm bg-card border border-destructive/30 rounded-xl p-8 text-center">
          <div class="text-destructive text-2xl mb-3">⚠</div>
          <h2 class="text-base font-medium text-foreground mb-2">
            Authentication failed
          </h2>
          <p class="text-sm text-muted-foreground mb-6">{error()}</p>
          <a
            href="/auth/login"
            class="inline-flex h-9 items-center px-4 rounded-lg bg-primary text-primary-foreground
                   text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Return to login
          </a>
        </div>
      ) : (
        <div class="flex flex-col items-center gap-3 text-muted-foreground">
          <div class="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p class="text-sm">Completing sign in…</p>
        </div>
      )}
    </div>
  );
}
